import { z } from 'zod';

type T = (key: string) => string;

export const createDeviceSchema = (t: T) =>
  z.object({
    name: z.string().min(1, t('nameRequired')).max(255, t('nameMax')),
    // Hidden form field carried through from discovery prefill — gives the
    // backend a stable handle to the physical scanner that survives renames.
    scanner_identifier: z.string().max(255).nullable().optional(),
    device_type: z
      .enum(['scanner', 'printer', 'barcode_scanner'])
      .default('scanner'),
    serial_number: z.string().max(50, t('serialNumberMax')).default(''),
    ip_address: z
      .union([
        z.literal(''),
        z
          .string()
          .regex(
            /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/,
            t('ipAddressInvalid')
          )
      ])
      .default(''),
    status: z.enum(['online', 'offline', 'maintenance']).default('online'),
    location: z.string().max(255, t('locationMax')).default(''),
    software_version: z.string().max(50, t('softwareVersionMax')).default(''),
    max_daily_capacity: z.preprocess(
      (val) =>
        val === '' || val === undefined || val === null
          ? undefined
          : Number(val),
      z
        .number()
        .int(t('maxDailyCapacityInt'))
        .min(1, t('maxDailyCapacityMin'))
        .max(10000, t('maxDailyCapacityMax'))
        .optional()
    )
  });

export const deviceSchema = createDeviceSchema((key) => {
  const defaults: Record<string, string> = {
    nameRequired: 'Name is required.',
    nameMax: 'Name must not exceed 255 characters.',
    serialNumberMax: 'Serial number must not exceed 50 characters.',
    ipAddressInvalid: 'Please enter a valid IPv4 address.',
    locationMax: 'Location must not exceed 255 characters.',
    softwareVersionMax: 'Software version must not exceed 50 characters.',
    maxDailyCapacityInt: 'Max daily capacity must be a whole number.',
    maxDailyCapacityMin: 'Max daily capacity must be at least 1.',
    maxDailyCapacityMax: 'Max daily capacity must not exceed 10000.'
  };
  return defaults[key] ?? key;
});

export type DeviceFormValues = z.infer<typeof deviceSchema>;
